import React, { FC, ChangeEvent, FormEvent, useState } from 'react'
import { useDispatch } from 'react-redux'
import useTypedSelector from 'interfaces/useTypedSelector'
import { Columns, Column } from 'components/base/grid/grid'
import { StrongLabel } from 'components/base/forms/label'
import { Input, Textarea, Wrapper } from 'components/base/forms/input'
import { Flex } from 'rebass'
import Button from 'components/base/buttons/buttons'
import toastMessage from 'components/utils/toasts/toasts'
import { HR, PSmall } from 'components/base/typography/misc'
import {
	handleFormChange,
	setInitialFormState,
	UserInfoFormData,
	UserInfoInitialFormData,
	initialState,
} from 'reducers/formReducer'
import { Toast, ToastMessage } from 'components/utils/toasts/toasts.styles'
import { Block } from 'components/base/sections/section'
import Checkbox from 'components/base/forms/checkbox'
import { Normal } from 'components/base/typography/misc'
import * as yup from 'yup'
import * as kennitala from 'kennitala'
import { extractValidationErrors } from 'utils/errors'
import { validatePhone, validateSchema } from 'utils/validation'

const schema = yup.object().shape({
	companySocialId: yup
		.string()
		.required('Kennitala fyrirtækis vantar')
		.test('companySocialId', 'Kennitala fyrirtækis er ógild', id => kennitala.isValid(id)),
	name: yup.string().required('Nafn vantar'),
	tel: yup
		.string()
		.required('Símanúmer vantar')
		.test('tel', 'Símanúmer er ógilt', tel => {
			const value = tel.split('+354')[1]
			return validatePhone(value)
		}),
	email: yup.string().email('Netfang vantar'),
	password: yup.string().required('Lykilorð vantar'),
	jobTitle: yup.string().optional(),
	comments: yup.string().optional(),
})

interface Props {
	onSubmit: (data: UserInfoFormData) => void
	formError: string
	submitText?: string
	submitPrimary?: boolean
	initialValues?: UserInfoInitialFormData
	hideKennitala?: boolean
	hideSubscriptionOption?: boolean
}

const UserInfoForm: FC<Props> = ({
	submitText,
	submitPrimary,
	initialValues,
	hideKennitala,
	hideSubscriptionOption,
	onSubmit,
	formError,
}) => {
	const [errors, setErrors] = useState<Record<string, string>>({})
	const dispatch = useDispatch()
	const {
		companySocialId,
		name,
		jobTitle,
		tel,
		email,
		password,
		confirmPassword,
		comments,
		postlistSelected,
	} = useTypedSelector(({ form }) => form)

	const handleFormInput = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>, id: string) => {
		if (errors[id]) {
			setErrors(prev => {
				const newErrors = { ...prev }
				delete newErrors[id]
				return newErrors
			})
		}
		dispatch(handleFormChange({ value: e.target.value, id }))
	}

	React.useEffect(() => {
		if (initialValues) dispatch(setInitialFormState(initialValues))
	}, [])

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault()

		const payload = {
			companySocialId,
			jobTitle,
			name,
			tel: `+354${tel}`,
			email,
			password,
			comments,
			postlistSelected,
		}

		const errors = await validateSchema(schema, payload)
		if (errors) {
			setErrors(errors)
			return
		}

		if (password !== confirmPassword) {
			toastMessage({
				type: 'warning',
				message: 'Lykilorðin passa ekki saman',
			})

			return
		}

		onSubmit(payload)
		dispatch(handleFormChange(initialState))
	}

	const handlePostList = (value: boolean) => {
		dispatch(handleFormChange({ value, id: 'postlistSelected' }))
	}

	return (
		<form onSubmit={handleSubmit}>
			<Columns>
				<Column tablet={1 / 2}>
					<StrongLabel mb="1.5rem" htmlFor="name">
						Nafn
					</StrongLabel>
					<Input
						hasError={!!errors.name}
						type="text"
						id="name"
						name="name"
						aria-label="Nafn"
						placeholder="Nafn"
						value={name}
						required
						onChange={e => handleFormInput(e, 'name')}
					/>
					{errors.name && (
						<PSmall mt="0.5rem" color="secondary">
							{errors.name}
						</PSmall>
					)}
				</Column>
				<Column tablet={1 / 2}>
					<StrongLabel mb="1.5rem" htmlFor="tel">
						Símanúmer
					</StrongLabel>
					<Wrapper hasError={!!errors.tel}>
						<Input
							type="tel"
							id="tel"
							name="tel"
							aria-label="Símanúmer"
							placeholder="Símanúmer"
							value={tel}
							required
							onChange={e => handleFormInput(e, 'tel')}
							hasPrefix={true}
						/>
					</Wrapper>
					{errors.tel && (
						<PSmall mt="0.5rem" color="secondary">
							{errors.tel}
						</PSmall>
					)}
					<PSmall mt="0.5rem">Án bila og bandstrika</PSmall>
				</Column>
				<Column tablet={1}>
					<StrongLabel mb="1.5rem" htmlFor="email">
						Netfang
					</StrongLabel>
					<Input
						hasError={!!errors.email}
						type="email"
						id="email"
						name="email"
						aria-label="Netfang"
						placeholder="Netfang"
						value={email}
						required
						onChange={e => handleFormInput(e, 'email')}
					/>
					{errors.email && (
						<PSmall mt="0.5rem" color="secondary">
							{errors.email}
						</PSmall>
					)}
				</Column>
				<Column tablet={1}>
					{!hideSubscriptionOption && (
						<Block mt="1rem">
							<Checkbox checked={postlistSelected} onClick={() => handlePostList(!postlistSelected)}>
								<Normal>Skrá mig á póstlista hjá Matarstræti</Normal>
							</Checkbox>
						</Block>
					)}
				</Column>
				<Column tablet={1 / 2}>
					<StrongLabel mt="1.5rem" mb="1.5rem" htmlFor="password">
						Lykilorð
					</StrongLabel>
					<Input
						hasError={!!errors.password}
						type="password"
						id="password"
						name="password"
						autoComplete="new-password"
						aria-label="Lykilorð"
						placeholder="Lykilorð"
						minLength={6}
						value={password}
						required
						pattern="^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\s).*$"
						onChange={e => handleFormInput(e, 'password')}
					/>
					{errors.password && (
						<PSmall mt="0.5rem" color="secondary">
							{errors.password}
						</PSmall>
					)}
					<PSmall mt="0.5rem">Minnst átta stafir, stór stafur, lítill stafur, tölustafur.</PSmall>
				</Column>
				<Column tablet={1 / 2}>
					<StrongLabel mt="1.5rem" mb="1.5rem" htmlFor="confirm-password">
						Staðfesta Lykilorð
					</StrongLabel>
					<Input
						hasError={!!errors.confirmPassword}
						type="password"
						id="confirm-password"
						name="confirm-password"
						autoComplete="new-password"
						aria-label="Staðfesta Lykilorð"
						placeholder="Staðfesta Lykilorð"
						minLength={8}
						value={confirmPassword}
						required
						pattern="^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?!.*\s).*$"
						onChange={e => handleFormInput(e, 'confirmPassword')}
					/>
					{errors.confirmPassword && (
						<PSmall mt="0.5rem" color="secondary">
							{errors.confirmPassword}
						</PSmall>
					)}
				</Column>
			</Columns>
			<HR mt="3rem" mb="3rem" />
			<Columns>
				{!hideKennitala && (
					<Column tablet={1}>
						<StrongLabel mb="1.5rem" htmlFor="company-social-id">
							Kennitala fyrirtækis
						</StrongLabel>
						<Input
							hasError={!!errors.companySocialId}
							type="text"
							id="company-social-id"
							name="company-social-id"
							aria-label="Kennitala fyrirtækis"
							value={companySocialId}
							required
							placeholder="Kennitala fyrirtækis"
							minLength={10}
							maxLength={10}
							onChange={e => handleFormInput(e, 'companySocialId')}
						/>
						{errors.companySocialId && (
							<PSmall mt="0.5rem" color="secondary">
								{errors.companySocialId}
							</PSmall>
						)}
					</Column>
				)}
				<Column tablet={1}>
					<StrongLabel mb="1.5rem" htmlFor="job-title">
						Starfstitill
					</StrongLabel>
					<Input
						type="text"
						id="job-title"
						name="job-title"
						aria-label="Starfstitill"
						value={jobTitle}
						placeholder="Starfstitill"
						onChange={e => handleFormInput(e, 'jobTitle')}
					/>
				</Column>
				<Column tablet={1}>
					<StrongLabel mb="1.5rem" mt="1.5rem" htmlFor="comments">
						Athugasemdir
					</StrongLabel>
					<Textarea
						id="comments"
						name="comments"
						aria-label="Athugasemdir"
						placeholder="Athugasemdir"
						value={comments}
						onChange={e => handleFormInput(e, 'comments')}
					/>
				</Column>
			</Columns>
			<Columns>
				<Column tablet={1 / 2}>
					{formError.length > 0 && (
						<Toast type="warning">
							<ToastMessage>{formError}</ToastMessage>
						</Toast>
					)}
				</Column>
				<Column tablet={1 / 2}>
					<Flex width={1} justifyContent="flex-end" mt="1.5rem">
						<Button type="submit" secondary={!submitPrimary} primary={submitPrimary}>
							{submitText ? submitText : 'Nýskrá'}
						</Button>
					</Flex>
				</Column>
			</Columns>
		</form>
	)
}

export default UserInfoForm
